import * as React from "react";

import usePaymentAccount from "app/hooks/usePaymentAccount";
import { ConnectPaymentMethod } from "app/models/paymentMethod";
import Button from "app/components/ui/Button";
import TextField from "app/components/ui/TextField";
import Header from "app/styles/Header";
import Text from "app/styles/Text";
import Card from "app/components/ui/Card";
import EnableTwoStepAuthModal from "app/components/EnableTwoStepAuthModal";
import { GenericResponse } from "app/api/types";

interface Props {
  method: ConnectPaymentMethod;
  onSuccess: (d: GenericResponse<{ token: string; message: string }>) => void;
}
const AddAccountForm = (props: Props) => {
  const { method, onSuccess } = props;

  const usernameField = method.fields.find((f) => f.name === "username");

  const [fieldValues, setFieldValues] =
    React.useState<Record<string, string>>();

  React.useEffect(() => {
    const initialValues = method.fields.reduce((acc, current) => {
      acc[current.name] = ((usernameField?.validator ?? "") as string).includes(
        "@"
      )
        ? "@"
        : "";
      return acc;
    }, {} as any);
    setFieldValues(initialValues);
  }, [method.fields, usernameField?.validator]);

  const updateFieldValue = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    const fieldName = e.target.name;

    const field = method.fields.find((f) => f.name === fieldName);
    const showPrefix = ((field?.validator ?? "") as string).includes("@");

    setFieldValues((f) => ({
      ...f,
      [e.target.name]: showPrefix ? `@${value.replaceAll("@", "")}` : value,
    }));
  };

  const { isInitializing: isLoading, initialize } = usePaymentAccount();

  const submitAccountData = () => {
    const fields = Object.entries(fieldValues ?? {}).map(([key, value]) => ({
      name: key,
      value: value,
    }));

    initialize(
      {
        payment_method: method.id,
        fields: fields,
      },
      { onSuccess: (data) => onSuccess(data.data) }
    );
  };

  const disableBtn = Object.values(fieldValues ?? {}).every(
    (value) => value !== ""
  );

  return (
    <>
      <Header semiBold tw="text-[28px]">
        {method.name}
      </Header>

      <Text subdued medium>
        {method.fields[0].message}
      </Text>

      <Card tw="mt-[24px]">
        <div tw="p-[20px] flex flex-col gap-[10px]">
          <div tw="border-b border-[rgba(0, 0, 0, 0.12)] dark:border-gray-800 pb-[16px] grid gap-y-[16px]">
            {fieldValues &&
              method.fields.map((field, index) => (
                <TextField
                  key={index}
                  label={field.display_name}
                  placeholder={field.hint_word}
                  value={fieldValues[field.name]}
                  name={field.name}
                  onChange={updateFieldValue}
                  autoFocus={index === 0}
                />
              ))}
          </div>

          <Button
            primary
            fullWidth
            onClick={submitAccountData}
            disabled={!disableBtn}
            loading={isLoading}
          >
            Continue
          </Button>
        </div>
      </Card>

      <EnableTwoStepAuthModal />
    </>
  );
};

export default AddAccountForm;
